import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, User, Calendar, MapPin, MessageSquare, Camera, CheckCircle2, XCircle, Edit, Trash2, ZoomIn, ZoomOut, MoreVertical, FileDown, History } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { getStatusColor, getStatusLabel, getPriorityColor, formatDate, formatDateBySetting, formatDateTimeBySetting, getRoleColor } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import PhotoLightbox from '../components/PhotoLightbox';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTranslation } from 'react-i18next';
import { generateTicketPDF } from '../utils/pdfGeneratorSimple';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const TicketDetail = () => {
  const { t } = useTranslation();
  const { ticketId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatorName, setCreatorName] = useState('');
  const [assignedUserNames, setAssignedUserNames] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [roleDisplayNames, setRoleDisplayNames] = useState({});
  const [floor, setFloor] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [pdfScale, setPdfScale] = useState(1.5);
  const pdfContainerRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: '',
    assigned_to: [],
    deadline: '',
    newPhotos: []
  });
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');

  useEffect(() => {
    fetchTicketData();
    fetchCategories();
    fetchUsers();
    fetchRoles();
    fetchSiteSettings();
  }, [ticketId, location.pathname]); // Refetch when navigating back to this page

  const fetchSiteSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings');
      setDateFormat(response.data.date_format || 'MM/DD/YYYY');
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
      setDateFormat('MM/DD/YYYY');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      const displayNameMap = {};
      response.data.forEach(role => {
        displayNameMap[role.name] = role.display_name;
      });
      setRoleDisplayNames(displayNameMap);
    } catch (error) {
      console.error('Failed to fetch roles');
    }
  };

  const fetchTicketData = async () => {
    try {
      const [ticketRes, commentsRes] = await Promise.all([
        axiosInstance.get(`/tickets/${ticketId}`),
        axiosInstance.get(`/tickets/${ticketId}/comments`)
      ]);
      
      const ticketData = ticketRes.data;
      setComments(commentsRes.data);
      
      // Fetch floor data for PDF viewer
      if (ticketData.floor_id) {
        try {
          const floorRes = await axiosInstance.get(`/floors/${ticketData.floor_id}`);
          setFloor(floorRes.data);
        } catch (error) {
          console.error('Failed to fetch floor data');
        }
      }
      
      // Fetch project data for PDF
      if (ticketData.project_id) {
        try {
          const projectRes = await axiosInstance.get(`/projects/${ticketData.project_id}`);
          setProject(projectRes.data);
        } catch (error) {
          console.error('Failed to fetch project data');
        }
      }
      
      // Fetch creator and assigned user names
      if (ticketData.created_by || ticketData.assigned_to) {
        try {
          const usersRes = await axiosInstance.get('/users');
          const usersList = usersRes.data;
          
          // Create users map for quick lookup
          const userMap = {};
          usersList.forEach(u => {
            userMap[u.id] = u;
          });
          setUsersMap(userMap);
          
          // Get creator name
          if (ticketData.created_by) {
            const creator = usersList.find(u => u.id === ticketData.created_by);
            if (creator) {
              setCreatorName(creator.name);
              // Add to ticket object for PDF
              ticketData.created_by_name = creator.name;
            }
          }
          
          // Get assigned user names
          if (ticketData.assigned_to && ticketData.assigned_to.length > 0) {
            const assignedUsers = usersList.filter(u => 
              ticketData.assigned_to.includes(u.id)
            );
            const assignedNames = assignedUsers.map(u => u.name);
            setAssignedUserNames(assignedNames);
            // Add to ticket object for PDF
            ticketData.assigned_to_names = assignedNames;
          } else {
            setAssignedUserNames([]);
            ticketData.assigned_to_names = [];
          }
        } catch (error) {
          console.error('Failed to fetch user names');
        }
      }
      
      // Set ticket state after enriching with names
      setTicket(ticketData);
    } catch (error) {
      toast.error(t('messages.error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axiosInstance.put(`/tickets/${ticketId}`, { status: newStatus });
      toast.success(t('messages.success.updated'));
      fetchTicketData();
    } catch (error) {
      toast.error(t('messages.error.failed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const formData = new FormData();
      formData.append('ticket_id', ticketId);
      formData.append('content', newComment);
      commentPhotos.forEach((photo) => {
        formData.append('photos', photo);
      });

      await axiosInstance.post('/comments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('messages.success.created'));
      setNewComment('');
      setCommentPhotos([]);
      fetchTicketData();
    } catch (error) {
      toast.error(t('messages.error.failed'));
    }
  };

  const handleApproveComment = async (commentId, approvalNote) => {
    try {
      await axiosInstance.post(`/comments/${commentId}/approve`, null, {
        params: { approval_note: approvalNote }
      });
      toast.success(t('comments.approved'));
      fetchTicketData();
    } catch (error) {
      toast.error(t('messages.error.failed'));
    }
  };

  const handleRejectComment = async (commentId, approvalNote) => {
    try {
      await axiosInstance.post(`/comments/${commentId}/reject`, null, {
        params: { approval_note: approvalNote }
      });
      toast.success(t('comments.rejected'));
      fetchTicketData();
    } catch (error) {
      toast.error(t('messages.error.failed'));
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info(t('tickets.generatingPDF') || 'Generating PDF...');
      
      // Get date format from settings
      const settingsRes = await axiosInstance.get('/settings');
      const dateFormat = settingsRes.data.date_format || 'MM/DD/YYYY';
      
      await generateTicketPDF(
        ticket,
        project,
        floor,
        comments,
        t,
        dateFormat,
        formatDateTimeBySetting,
        formatDateBySetting,
        getFileUrl,
        roleDisplayNames,
        usersMap
      );
      
      toast.success(t('tickets.pdfDownloaded') || 'PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('tickets.pdfError') || 'Failed to generate PDF');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(t('comments.deleteComment'))) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/comments/${commentId}`);
      toast.success(t('messages.success.deleted'));
      fetchTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Convert /uploads/photos/filename to /api/files/photos/filename for proper routing
  const getFileUrl = (path) => path ? path.replace('/uploads/', '/api/files/') : '';
  const canUpdateStatus = hasPermission('approve_ticket');
  const isAdmin = user?.role === 'admin';
  const isPM = user?.role === 'project_manager';
  const isEngineer = user?.role && user.role.toLowerCase().includes('engineer');
  
  // Edit permission based on status
  const canEdit = ticket?.status === 'closed' ? false : (isAdmin || isPM || hasPermission('edit_ticket'));
  
  // Delete permission based on status
  const canDelete = ticket?.status === 'closed' ? false : 
                   (ticket?.status === 'new' ? (isAdmin || isPM) : 
                   (ticket?.status === 'in_progress' || ticket?.status === 'fixed') ? isAdmin : false);

  // Get return path from navigation state
  const returnTo = location.state?.returnTo || '/tickets';
  const returnLabel = location.state?.returnLabel || 'Tickets';

  const openEditDialog = () => {
    setEditFormData({
      title: ticket.title,
      description: ticket.description,
      category: ticket.category,
      priority: ticket.priority,
      assigned_to: ticket.assigned_to || [],
      deadline: ticket.deadline || '',
      newPhotos: []
    });
    setEditDialogOpen(true);
  };

  const handleEditTicket = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      // For basic field updates (title, description, etc.) - use /edit endpoint with FormData
      const formData = new FormData();
      if (editFormData.title) formData.append('title', editFormData.title);
      if (editFormData.description) formData.append('description', editFormData.description);
      if (editFormData.category) formData.append('category', editFormData.category);
      if (editFormData.priority) formData.append('priority', editFormData.priority);
      if (editFormData.deadline) formData.append('deadline', editFormData.deadline);
      
      editFormData.newPhotos.forEach((photo) => {
        formData.append('photos', photo);
      });

      const config = {
        headers: {
          'Content-Type': undefined
        }
      };
      await axiosInstance.put(`/tickets/${ticketId}/edit`, formData, config);
      
      // For assigned_to update - use the main update endpoint with JSON (only if admin/PM)
      if (user?.role === 'admin' || user?.role === 'project_manager') {
        await axiosInstance.put(`/tickets/${ticketId}`, {
          assigned_to: editFormData.assigned_to
        });
      }
      
      toast.success(t('messages.success.updated'));
      setEditDialogOpen(false);
      fetchTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.failed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePhoto = async (photoIndex) => {
    if (!window.confirm(t('tickets.deleteTicket'))) return;
    
    try {
      await axiosInstance.delete(`/tickets/${ticketId}/photo/${photoIndex}`);
      toast.success(t('messages.success.deleted'));
      fetchTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.deleteFailed'));
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await axiosInstance.delete(`/tickets/${ticketId}`);
      toast.success(t('messages.success.deleted'));
      navigate(returnTo);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.deleteFailed'));
    }
  };

  const handlePdfLoad = () => {
    // Center the view on the ticket marker after PDF loads
    setTimeout(() => {
      if (pdfContainerRef.current && ticket) {
        const container = pdfContainerRef.current;
        const pdfElement = container.querySelector('.react-pdf__Page');
        
        if (pdfElement) {
          const pdfWidth = pdfElement.offsetWidth;
          const pdfHeight = pdfElement.offsetHeight;
          
          // Calculate marker position in pixels
          const markerX = (ticket.coordinate_x / 100) * pdfWidth;
          const markerY = (ticket.coordinate_y / 100) * pdfHeight;
          
          // Center the marker in the viewport
          const containerWidth = container.offsetWidth;
          const containerHeight = container.offsetHeight;
          
          container.scrollLeft = markerX - (containerWidth / 2);
          container.scrollTop = markerY - (containerHeight / 2);
        }
      }
    }, 100);
  };

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <div>
        <button 
          onClick={() => navigate(returnTo)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('navigation.backTo')} {returnLabel}
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 mr-4">
            {/* Mobile: Code and Status on top in one row */}
            <div className="flex items-center gap-2 mb-2 md:hidden">
              {ticket?.ticket_code && (
                <code className="text-xs font-mono px-2 py-1 bg-primary/10 text-primary rounded border border-primary/20">
                  {ticket.ticket_code}
                </code>
              )}
              <Badge className={`${getStatusColor(ticket?.status)} text-xs px-2 py-0.5`}>
                {t(`tickets.statuses.${ticket?.status === 'in_progress' ? 'inProgress' : ticket?.status}`)}
              </Badge>
            </div>

            {/* Desktop: Title can wrap to two rows, with code and status below */}
            <div className="hidden md:block">
              <h1 className="text-4xl font-black tracking-tight break-words">{ticket?.title}</h1>
              <div className="flex items-center gap-3 mt-2">
                {ticket?.ticket_code && (
                  <code className="text-lg font-mono px-3 py-1 bg-primary/10 text-primary rounded-lg border border-primary/20">
                    {ticket.ticket_code}
                  </code>
                )}
                <Badge className={`${getStatusColor(ticket?.status)} text-lg px-4 py-1`}>
                  {t(`tickets.statuses.${ticket?.status === 'in_progress' ? 'inProgress' : ticket?.status}`)}
                </Badge>
              </div>
            </div>

            {/* Mobile: Responsive smaller title that fits in one row */}
            <h1 className="text-xl font-bold tracking-tight truncate md:hidden">{ticket?.title}</h1>
            
            <p className="text-muted-foreground mt-2 text-xs md:text-sm">{t('tickets.ticketID')}: <code className="text-xs font-mono">{ticket?.id}</code></p>
          </div>
          
          {/* Action Buttons Container */}
          <div className="flex-shrink-0">
            {/* Desktop: Show Download PDF, Edit and Delete buttons */}
            <div className="hidden md:flex items-start gap-2">
              <Button variant="outline" onClick={handleDownloadPDF}>
                <FileDown className="w-4 h-4 mr-2" />
                {t('tickets.downloadPDF')}
              </Button>
              {canEdit && (
                <Button variant="outline" onClick={openEditDialog}>
                  <Edit className="w-4 h-4 mr-2" />
                  {t('common.edit')}
                </Button>
              )}
              {canDelete && (
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('common.delete')}
                </Button>
              )}
            </div>

            {/* Mobile: Three-dot dropdown menu */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDownloadPDF}>
                    <FileDown className="w-4 h-4 mr-2" />
                    {t('tickets.downloadPDF')}
                  </DropdownMenuItem>
                  {canEdit && (
                    <DropdownMenuItem onClick={openEditDialog}>
                      <Edit className="w-4 h-4 mr-2" />
                      {t('common.edit')}
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem 
                      onClick={() => setDeleteDialogOpen(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>{t('tickets.description')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{ticket?.description}</p>
            </CardContent>
          </Card>

          {/* Photos */}
          {ticket?.photos && ticket.photos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('tickets.photos')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-[5px]">
                  {ticket.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={getFileUrl(photo)}
                        alt={`Ticket photo ${index + 1}`}
                        className="max-h-48 max-w-full rounded-lg border border-border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setLightboxPhotos(ticket.photos.map(p => getFileUrl(p)));
                          setLightboxIndex(index);
                          setLightboxOpen(true);
                        }}
                      />
                      {canEdit && (
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePhoto(index);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle>{t('comments.activityAndComments')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {comments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('comments.noCommentsYet')}</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className={`space-y-3 p-4 rounded-lg relative ${
                    comment.approval_status === 'pending' ? 'bg-yellow-50 border-2 border-yellow-300' : 
                    comment.approval_status === 'approved' ? 'bg-green-50 border border-green-200' :
                    comment.approval_status === 'rejected' ? 'bg-red-50 border border-red-200' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                          <span className="text-xs font-bold text-primary-foreground">
                            {comment.user_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={`font-semibold text-sm ${
                              comment.approval_status === 'pending' 
                                ? 'text-gray-900 dark:text-black' 
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>{comment.user_name}</p>
                            {comment.user_role && (
                              <Badge className={`${getRoleColor(comment.user_role)} text-xs capitalize border-0`}>
                                {roleDisplayNames[comment.user_role] || comment.user_role.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{formatDateTimeBySetting(comment.created_at, dateFormat)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {comment.approval_status && (
                          <Badge className={
                            comment.approval_status === 'pending' ? 'bg-yellow-500' :
                            comment.approval_status === 'approved' ? 'bg-green-500' :
                            'bg-red-500'
                          }>
                            {comment.approval_status === 'pending' ? `⏳ ${t('comments.pendingApproval')}` :
                             comment.approval_status === 'approved' ? `✓ ${t('comments.approved')}` :
                             `✗ ${t('comments.rejected')}`}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground pl-11">{comment.content}</p>
                    {comment.photos && comment.photos.length > 0 && (
                      <div className="flex flex-wrap gap-[5px] pl-11">
                        {comment.photos.map((photo, index) => (
                          <img
                            key={index}
                            src={getFileUrl(photo)}
                            alt={`Comment photo ${index + 1}`}
                            className="max-h-32 max-w-full rounded-lg border border-border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              setLightboxPhotos(comment.photos.map(p => getFileUrl(p)));
                              setLightboxIndex(index);
                              setLightboxOpen(true);
                            }}
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Approval Actions */}
                    {/*  Dùng hasPermission('approve_ticket') */}
                    {comment.approval_status === 'pending' && hasPermission('approve_ticket') && (
                      <div className="flex gap-2 pl-11 pt-2" data-testid="approval-actions">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => {
                            setSelectedComment(comment);
                            setApprovalAction('approve');
                            setApprovalDialogOpen(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          {t('comments.approveToFixed')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedComment(comment);
                            setApprovalAction('reject');
                            setApprovalDialogOpen(true);
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          {t('common.reject')}
                        </Button>
                      </div>
                    )}
                    
                    {/* Approval Note Display */}
                    {(comment.approval_status === 'approved' || comment.approval_status === 'rejected') && comment.approved_by && (
                      <div className="pl-11 pt-2 text-sm border-t border-border/50 mt-2 pt-2">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="font-semibold">
                            {usersMap[comment.approved_by]?.name || t('users.unknown')}
                          </span>
                          <span>{comment.approval_status === 'approved' ? t('comments.approvedThis') : t('comments.rejectedThis')}</span>
                        </div>
                        {comment.approval_note && (
                          <p className="mt-1 italic text-muted-foreground">
                            <strong>{t('comments.note')}:</strong> {comment.approval_note}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Delete Button - Bottom Right */}
                    {user?.role === 'admin' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="absolute bottom-2 right-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                        title="Delete comment"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}

              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-3 pt-4 border-t border-border">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t('comments.addComment')}
                  rows={3}
                  data-testid="comment-input"
                />
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setCommentPhotos(Array.from(e.target.files))}
                    className="flex-1"
                    data-testid="comment-photos-input"
                  />
                  <Button type="submit" disabled={!newComment.trim()} data-testid="submit-comment-button">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('comments.comment')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {(canUpdateStatus || isEngineer) && (
            <Card>
              <CardHeader>
                <CardTitle>{t('tickets.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {/* Approve/Reject - Only for Admin/PM/Inspector when status is Fixed */}
                {canUpdateStatus && ticket?.status === 'fixed' && (
                  <>
                    <Button
                      onClick={() => handleStatusUpdate('closed')}
                      disabled={updating}
                      className="gap-2 bg-green-500 hover:bg-green-600"
                      data-testid="approve-button"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {t('tickets.approveAndClose')}
                    </Button>
                    <Button
                      onClick={() => handleStatusUpdate('rejected')}
                      disabled={updating}
                      variant="destructive"
                      className="gap-2"
                      data-testid="reject-button"
                    >
                      <XCircle className="w-4 h-4" />
                      {t('tickets.rejectFix')}
                    </Button>
                  </>
                )}
                
                {/* Start Progress - Available for everyone when status is New */}
                {ticket?.status === 'new' && (
                  <Button
                    onClick={() => handleStatusUpdate('in_progress')}
                    disabled={updating}
                    className="gap-2"
                    data-testid="start-progress-button"
                  >
                    {t('tickets.startProgress')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('tickets.ticketTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{t('tickets.priority')}</Label>
                <Badge variant="outline" className={`capitalize ${getPriorityColor(ticket?.priority)}`}>
                  {t(`tickets.priorities.${ticket?.priority}`)}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{t('tickets.category')}</Label>
                <p className="text-sm capitalize">{ticket?.category}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{t('tickets.location')}</Label>
                <button
                  onClick={() => {
                    setLocationDialogOpen(true);
                    setPdfScale(1.5); // Reset scale when opening
                  }}
                  className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer"
                >
                  <MapPin className="w-4 h-4" />
                  <code className="text-xs font-mono">
                    ({ticket?.coordinate_x.toFixed(1)}%, {ticket?.coordinate_y.toFixed(1)}%)
                  </code>
                  <span className="text-xs text-muted-foreground ml-1">({t('floors.clickToView')})</span>
                </button>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{t('tickets.createdBy')}</Label>
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {creatorName || t('common.loading')}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{t('tickets.assignedTo')}</Label>
                <div className="flex flex-col gap-1 text-sm">
                  {assignedUserNames.length > 0 ? (
                    assignedUserNames.map((name, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {name}
                      </div>
                    ))
                  ) : (
                    <span className="text-muted-foreground italic">{t('tickets.notAssigned')}</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase text-muted-foreground">{t('tickets.createdAt')}</Label>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {formatDateTimeBySetting(ticket?.created_at, dateFormat)}
                </div>
              </div>
              {ticket?.deadline && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase text-muted-foreground">{t('tickets.deadline')}</Label>
                  <p className="text-sm">{formatDateBySetting(ticket.deadline, dateFormat)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* --- KHUNG HISTORY LOG MỚI --- */}
          <Card>
            <CardHeader className="pb-3 pt-5">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <History className="w-4 h-4" />
                {/* Sửa dòng này để dùng biến t() */}
                {t('tickets.history')} 
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative border-l border-gray-200 dark:border-gray-700 ml-2 space-y-6">
                {ticket?.history && ticket.history.length > 0 ? (
                  ticket.history.map((item, index) => {
                    // Hàm helper để map từ text Backend sang key Ngôn ngữ
                    const getActionLabel = (actionText) => {
                      const map = {
                        'Created Ticket': 'created',
                        'Started Progress': 'startedProgress',
                        'Approved & Closed': 'approvedClosed',
                        'Rejected Fix': 'rejectedFix',
                        'Approved Fix (via Comment)': 'approvedFix',
                        'Rejected Fix (via Comment)': 'rejectedFixComment',
                        'Updated Status': 'statusUpdated'
                      };
                      // Nếu tìm thấy key thì dịch, không thì hiện text gốc
                      const key = map[actionText];
                      return key ? t(`tickets.historyActions.${key}`) : actionText;
                    };

                    return (
                      <div key={index} className="ml-4 relative">
                        {/* Chấm tròn trạng thái (Giữ nguyên) */}
                        <div className={`absolute -left-[21px] mt-1.5 w-2.5 h-2.5 rounded-full border border-background ring-1 ring-border ${
                          item.new_status === 'new' ? 'bg-red-500' :
                          item.new_status === 'in_progress' ? 'bg-yellow-500' :
                          item.new_status === 'fixed' ? 'bg-green-500' :
                          item.new_status === 'closed' ? 'bg-gray-500' :
                          item.new_status === 'rejected' ? 'bg-orange-500' : 'bg-blue-500'
                        }`} />
                        
                        {/* Nội dung Log (Đã dịch) */}
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-foreground">
                            {getActionLabel(item.action)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t('tickets.historyActions.by')} <span className="font-medium text-foreground">{item.user_name}</span>
                          </span>
                          <span className="text-[10px] text-muted-foreground mt-0.5">
                            {formatDateTimeBySetting(item.timestamp, dateFormat)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-muted-foreground ml-4 italic">
                    {t('comments.noCommentsYet') || 'No history recorded'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Ticket Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('tickets.editTicket')}</DialogTitle>
            <DialogDescription>
              {ticket?.status === 'new' 
                ? t('tickets.editTicket')
                : t('tickets.editTicket')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTicket} className="space-y-4">
            {ticket?.status === 'new' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-title">{t('tickets.ticketTitle')}</Label>
                  <Input
                    id="edit-title"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">{t('tickets.description')}</Label>
                  <Textarea
                    id="edit-description"
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-category">{t('tickets.category')}</Label>
                    <Select value={editFormData.category} onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.length > 0 ? (
                          categories.map((category) => (
                            <SelectItem key={category.id} value={category.name}>
                              {category.name}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="structural">Structural</SelectItem>
                            <SelectItem value="electrical">Electrical</SelectItem>
                            <SelectItem value="plumbing">Plumbing</SelectItem>
                            <SelectItem value="fire_safety">Fire Safety</SelectItem>
                            <SelectItem value="hvac">HVAC</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-priority">{t('tickets.priority')}</Label>
                    <Select value={editFormData.priority} onValueChange={(value) => setEditFormData({ ...editFormData, priority: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">{t('tickets.priorities.urgent')}</SelectItem>
                        <SelectItem value="high">{t('tickets.priorities.high')}</SelectItem>
                        <SelectItem value="medium">{t('tickets.priorities.medium')}</SelectItem>
                        <SelectItem value="low">{t('tickets.priorities.low')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
            {(user?.role === 'admin' || user?.role === 'project_manager') && (
              <div className="space-y-2">
                <Label>{t('tickets.assignedTo')}</Label>
                {(ticket?.status === 'in_progress' || ticket?.status === 'fixed') && (
                  <p className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded border border-yellow-200">
                    ⚠️ For tickets in progress, you can only add new users. Previously assigned users are locked and cannot be removed.
                  </p>
                )}
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {allUsers.length > 0 ? (
                    allUsers.map((usr) => {
                      const isAlreadyAssigned = ticket?.assigned_to?.includes(usr.id);
                      const isLocked = isAlreadyAssigned && (ticket?.status === 'in_progress' || ticket?.status === 'fixed');
                      
                      return (
                        <label 
                          key={usr.id} 
                          className={`flex items-center gap-2 p-2 rounded ${isLocked ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                        >
                          <input
                            type="checkbox"
                            checked={editFormData.assigned_to.includes(usr.id)}
                            disabled={isLocked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditFormData({ 
                                  ...editFormData, 
                                  assigned_to: [...editFormData.assigned_to, usr.id] 
                                });
                              } else {
                                setEditFormData({ 
                                  ...editFormData, 
                                  assigned_to: editFormData.assigned_to.filter(id => id !== usr.id) 
                                });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span className={`text-sm ${isLocked ? 'text-gray-500' : ''}`}>{usr.name}</span>
                          {isLocked && (
                            <Badge variant="secondary" className="text-xs">🔒 Locked</Badge>
                          )}
                          <Badge className={`${getRoleColor(usr.role)} text-xs ml-auto capitalize border-0`}>
                            {roleDisplayNames[usr.role] || usr.role.replace('_', ' ')}
                          </Badge>
                        </label>
                      );
                    })
                  ) : (
                    <p className="text-sm text-muted-foreground text-center">No users available</p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {ticket?.status === 'new' 
                    ? 'Select users to assign to this ticket' 
                    : 'You can add more users, but cannot remove existing assignments'}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-photos">{t('tickets.addMorePhotos')}</Label>
              <Input
                id="edit-photos"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setEditFormData({ ...editFormData, newPhotos: Array.from(e.target.files) })}
              />
              <p className="text-xs text-muted-foreground">{t('tickets.addMorePhotos')}</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="flex-1" disabled={updating}>
                {updating ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('tickets.deleteTicket')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('tickets.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTicket} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('tickets.deleteTicket')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalAction === 'approve' ? t('comments.approveFix') : t('comments.rejectFix')}
            </DialogTitle>
            <DialogDescription>
              {approvalAction === 'approve' 
                ? t('comments.approveFix')
                : t('comments.rejectFix')
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="approval-note">{t('comments.addNote')}</Label>
              <Textarea
                id="approval-note"
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                placeholder={t('comments.addComment')}
                rows={3}
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setApprovalDialogOpen(false);
                setApprovalNote('');
                setSelectedComment(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant={approvalAction === 'approve' ? 'default' : 'destructive'}
              onClick={() => {
                if (approvalAction === 'approve') {
                  handleApproveComment(selectedComment.id, approvalNote);
                } else {
                  handleRejectComment(selectedComment.id, approvalNote);
                }
                setApprovalDialogOpen(false);
                setApprovalNote('');
                setSelectedComment(null);
              }}
            >
              {approvalAction === 'approve' ? t('common.approve') : t('common.reject')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      {lightboxOpen && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Location PDF Viewer Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>{t('tickets.ticketLocation')}</DialogTitle>
            <DialogDescription>
              {floor?.name} - {t('tickets.ticketPosition')} ({ticket?.coordinate_x.toFixed(1)}%, {ticket?.coordinate_y.toFixed(1)}%)
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden px-6 pb-6 relative">
            {/* Zoom Controls */}
            <div className="absolute top-4 right-10 z-50 flex flex-col items-center gap-2 bg-background/95 backdrop-blur-sm border-2 border-border rounded-lg p-2 shadow-xl">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setPdfScale(Math.min(2, pdfScale + 0.1))} 
                className="h-10 w-10"
              >
                <ZoomIn className="w-5 h-5" />
              </Button>
              <span className="text-xs font-mono font-bold w-10 text-center">{Math.round(pdfScale * 100)}%</span>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setPdfScale(Math.max(0.5, pdfScale - 0.1))} 
                className="h-10 w-10"
              >
                <ZoomOut className="w-5 h-5" />
              </Button>
            </div>

            <div ref={pdfContainerRef} className="w-full h-full overflow-auto bg-muted/30 rounded-lg relative">
              <div className="relative inline-block" data-pdf-floor-plan>
                {floor?.pdf_path && (
                  <Document
                    file={floor.pdf_path.replace('/uploads/', '/api/files/')}
                    onLoadSuccess={handlePdfLoad}
                    loading={
                      <div className="flex items-center justify-center h-96">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      scale={pdfScale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}
                
                {/* Ticket Marker */}
                {ticket && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: `${ticket.coordinate_x}%`,
                      top: `${ticket.coordinate_y}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}
                  >
                    <div className="relative">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-4 border-white ${getStatusColor(ticket.status)} animate-pulse`}
                      >
                        <MapPin className="w-8 h-8 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetail;